// Direct port of ~/Desktop/calyx-erp/src/components/deals/DealSubRow/InvoiceDetailPanel.tsx
// re-wired to React Query mutations and the calyx-tool ProductPicker (which
// receives `products` as a prop instead of importing them statically).
// All monetary values are stored in cents; UI inputs are dollar-denominated
// and converted at the input/output boundary.

import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { Deal } from '../types/deal'
import type { Invoice, InvoiceItem } from '../types/invoice'
import { findProductItem, ProductPicker } from '../modals/ProductPicker'
import { useProductsQuery } from '../hooks/useProductsQuery'
import { useUpdateInvoiceMutation } from '../hooks/useUpdateInvoiceMutation'
import { centsToDollarString, dollarStringToCents, formatCents } from '../utils/formatCents'
import { toInvoiceItemDto } from '../utils/invoiceItemDto'
import { computeLineTotalCents, computeSubtotalCents } from '../utils/invoiceMath'

interface Props {
  deal: Deal
  invoice: Invoice
  invoiceIndex: number
  invoices: Invoice[]
  focusPaid?: boolean
  onFocusPaidConsumed?: () => void
  onPaidBlur?: () => void
}

export const InvoiceDetailPanel: React.FunctionComponent<Props> = (props) => {
  const {
    deal,
    invoice,
    invoiceIndex,
    invoices,
    focusPaid,
    onFocusPaidConsumed,
    onPaidBlur,
  } = props
  const updateMutation = useUpdateInvoiceMutation()
  const productsQuery = useProductsQuery()
  const productGroups = productsQuery.data ?? []
  const [pickerOpenIdx, setPickerOpenIdx] = useState<number | null>(null)
  const paidInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!focusPaid) return
    const element = paidInputRef.current
    if (!element) return
    element.focus()
    element.select()
    onFocusPaidConsumed?.()
  }, [focusPaid, onFocusPaidConsumed])

  const items = invoice.items || []
  const isStatusLocked =
    invoice.status === 'Voided' || invoice.status === 'Scheduled' || invoice.status === 'Draft'
  const canEditMetadata = invoice.status !== 'Voided' && invoice.status !== 'Scheduled'
  const canEditItems = canEditMetadata && invoice.origin === 'manual'

  const subtotalCents = useMemo(() => computeSubtotalCents(items), [items])

  const prevInv = invoiceIndex > 0 ? invoices[invoiceIndex - 1] : null
  const prevHasOutstanding = !!prevInv && prevInv.status === 'Partially Paid'

  const autoCarryCents = useMemo(() => {
    if (!prevHasOutstanding || !prevInv) return 0
    if (typeof prevInv.outstandingCents === 'number') return prevInv.outstandingCents
    const priorTotal = prevInv.totalCents ?? 0
    const priorPaid = prevInv.paidAmountCents ?? 0
    return Math.max(0, priorTotal - priorPaid)
  }, [prevHasOutstanding, prevInv])

  const showCarry = autoCarryCents > 0

  const autoCreditCents = useMemo(() => {
    if (!prevInv || prevHasOutstanding) return 0
    const previousPaid = prevInv.paidAmountCents ?? 0
    const previousTotal = prevInv.totalCents ?? 0
    return Math.max(0, previousPaid - previousTotal)
  }, [prevInv, prevHasOutstanding])

  const totalCents = showCarry
    ? subtotalCents + autoCarryCents
    : autoCreditCents > 0
      ? Math.max(0, subtotalCents - autoCreditCents)
      : invoice.totalCents ?? subtotalCents

  const paidCents =
    typeof invoice.paidAmountCents === 'number'
      ? invoice.paidAmountCents
      : invoice.status === 'Paid'
        ? totalCents
        : 0
  const outstandingCents = Math.max(0, totalCents - paidCents)
  const fullyPaid = outstandingCents === 0 && paidCents > 0

  const payBg = fullyPaid ? '#ecfdf5' : paidCents > 0 ? '#fff7ed' : '#f8fafc'
  const payBorder = fullyPaid ? '#a7f3d0' : paidCents > 0 ? '#fed7aa' : '#e2e8f0'
  const payAccent = fullyPaid ? '#047857' : paidCents > 0 ? '#c2410c' : '#475569'

  const yearLabel = invoice.year ? ' · Year ' + invoice.year : ''

  function commitItems(nextItems: InvoiceItem[]): void {
    updateMutation.mutate({
      dealId: deal.id,
      invoiceId: invoice.id,
      patch: { items: nextItems },
      body: { items: nextItems.map(toInvoiceItemDto) },
    })
  }

  function updateDiscount(idx: number, value: number): void {
    const item = items[idx]
    if (!item) return
    const discountPercent = Number.isFinite(value) ? Math.max(0, value) : 0
    const nextItems = items.map((entry, index) =>
      index === idx ? { ...entry, discountPercent } : entry,
    )
    commitItems(nextItems)
  }

  function updateQty(idx: number, value: number): void {
    const item = items[idx]
    if (!item) return
    const qty = Number.isFinite(value) ? Math.max(1, Math.floor(value)) : 1
    const nextItems = items.map((entry, index) =>
      index === idx ? { ...entry, qty } : entry,
    )
    commitItems(nextItems)
  }

  function updateItemProduct(idx: number, productValue: string): void {
    const item = items[idx]
    if (!item) return
    const product = findProductItem(productGroups, productValue)
    if (!product) return
    const nextItems = items.map((entry, index) =>
      index === idx
        ? {
            ...entry,
            productKey: productValue,
            productName: product.name,
            rateCents: product.priceCents,
            sub: product.sub,
          }
        : entry,
    )
    commitItems(nextItems)
  }

  function removeItem(idx: number): void {
    commitItems(items.filter((_, index) => index !== idx))
  }

  function addBlankItem(): void {
    const blank: InvoiceItem = {
      productName: '',
      qty: 1,
      rateCents: 0,
      discountPercent: 0,
      amountCents: 0,
    }
    commitItems([...items, blank])
  }

  // Live-recalculated paidAmount + status while the user types. We only fire
  // the mutation on blur so each keystroke does not hit React Query / the
  // mock store. UI input is dollar-denominated; persisted value is cents.
  const [paidDraftCents, setPaidDraftCents] = useState<number | null>(null)
  const draftedPaidCents = paidDraftCents ?? paidCents
  const draftedOutstandingCents = Math.max(0, totalCents - draftedPaidCents)

  function commitPaid(valueCents: number): void {
    const paidAmountCents = Number.isFinite(valueCents) ? Math.max(0, valueCents) : 0
    let nextStatus: Invoice['status'] = invoice.status
    if (!isStatusLocked) {
      if (paidAmountCents === 0) nextStatus = 'Unpaid'
      else if (paidAmountCents >= totalCents) nextStatus = 'Paid'
      else nextStatus = 'Partially Paid'
    }
    updateMutation.mutate({
      dealId: deal.id,
      invoiceId: invoice.id,
      patch: {
        paidAmountCents,
        status: nextStatus,
      },
      body: { paidAmountCents, status: nextStatus },
    })
    setPaidDraftCents(null)
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{invoice.invoiceNumber}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {invoice.period.label}
            {yearLabel}
            {invoice.isCombinedRenewal ? (
              <>
                {' · '}
                <span style={{ color: '#10b981' }}>Combined Renewal</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      {invoice.sentMeta ? (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: 6,
            fontSize: 12,
            color: '#047857',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width={12}
            height={12}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          Sent to <strong>{invoice.sentMeta.to}</strong> on{' '}
          {new Date(invoice.sentMeta.at).toLocaleString('en-US')}
        </div>
      ) : null}

      <table className="inv-pricing-table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Item</th>
            <th style={{ textAlign: 'right', width: 80 }}>Qty</th>
            <th style={{ textAlign: 'right', width: 180 }}>Rate</th>
            <th style={{ textAlign: 'right', width: 140 }}>Discount</th>
            <th style={{ textAlign: 'right', width: 120 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const hasDiscount = (item.discountPercent || 0) > 0
            return (
              <tr key={idx}>
                <td>
                  {canEditItems ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%' }}>
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <ProductPicker
                          value={item.productKey || ''}
                          onChange={(value) => updateItemProduct(idx, value)}
                          isOpen={pickerOpenIdx === idx}
                          onOpenChange={(open) => setPickerOpenIdx(open ? idx : null)}
                          products={productGroups}
                        />
                      </div>
                      <button
                        type="button"
                        title="Remove line"
                        onClick={(event) => {
                          event.stopPropagation()
                          removeItem(idx)
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          padding: 4,
                          fontSize: 14,
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <>
                      {item.productName}
                      {item.fromAddon ? (
                        <span
                          style={{
                            fontSize: 10,
                            color: '#f59e0b',
                            background: '#fffbeb',
                            padding: '1px 6px',
                            borderRadius: 3,
                            marginLeft: 4,
                          }}
                        >
                          from add-on
                        </span>
                      ) : null}
                    </>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {canEditItems ? (
                    <input
                      type="number"
                      value={item.qty}
                      min={1}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => updateQty(idx, Number(event.target.value))}
                      style={{
                        width: 56,
                        height: 28,
                        border: '1px solid #e5e5ea',
                        borderRadius: 4,
                        padding: '0 6px',
                        fontSize: 12,
                        textAlign: 'right',
                        fontFamily: 'inherit',
                      }}
                    />
                  ) : (
                    item.qty
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{formatCents(item.rateCents)}</td>
                <td style={{ textAlign: 'right' }}>
                  {canEditMetadata ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <input
                        type="number"
                        value={item.discountPercent || 0}
                        min={0}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => updateDiscount(idx, Number(event.target.value))}
                        style={{
                          width: 54,
                          height: 28,
                          border: `1px solid ${hasDiscount ? '#fde68a' : '#e5e5ea'}`,
                          borderRadius: 4,
                          padding: '0 4px',
                          fontSize: 12,
                          textAlign: 'right',
                          background: hasDiscount ? '#fffbeb' : 'white',
                          color: hasDiscount ? '#92400e' : '#1d1d1f',
                          fontFamily: 'inherit',
                        }}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0 6px',
                          fontSize: 11,
                          color: hasDiscount ? '#92400e' : '#6e6e73',
                          background: hasDiscount ? '#fffbeb' : 'white',
                          border: `1px solid ${hasDiscount ? '#fde68a' : '#e5e5ea'}`,
                          borderRadius: 4,
                          height: 28,
                          lineHeight: '28px',
                        }}
                      >
                        %
                      </span>
                    </div>
                  ) : (
                    <span style={{ color: hasDiscount ? '#f59e0b' : '#64748b' }}>
                      {hasDiscount ? `-${item.discountPercent}%` : '—'}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {formatCents(computeLineTotalCents(item))}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          {showCarry ? (
            <tr style={{ color: '#b45309', background: '#fffbeb' }}>
              <td colSpan={4} style={{ padding: '10px 12px' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  Previous balance due
                  {prevInv ? (
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#92400e' }}>
                      <span style={{ opacity: 0.75 }}>from</span> {prevInv.invoiceNumber}
                    </span>
                  ) : null}
                </span>
              </td>
              <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600 }}>
                +{formatCents(autoCarryCents)}
              </td>
            </tr>
          ) : null}
          {autoCreditCents > 0 ? (
            <tr style={{ color: '#166534', background: '#ecfdf5' }}>
              <td colSpan={4} style={{ padding: '10px 12px', fontSize: 13, fontWeight: 500 }}>
                Credit applied (overpayment from {prevInv?.invoiceNumber})
              </td>
              <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#166534' }}>
                −{formatCents(autoCreditCents)}
              </td>
            </tr>
          ) : null}
          <tr style={{ fontSize: 16, fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
            <td colSpan={4}>Total Due</td>
            <td style={{ textAlign: 'right' }}>{formatCents(totalCents)}</td>
          </tr>
        </tfoot>
      </table>

      {canEditItems ? (
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              addBlankItem()
            }}
            style={{
              fontSize: 12,
              color: '#475569',
              background: 'transparent',
              border: '1px dashed #cbd5e1',
              borderRadius: 6,
              padding: '6px 10px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Add product
          </button>
        </div>
      ) : null}

      <div
        className="inv-payment-card"
        style={{
          marginTop: 12,
          padding: '12px 16px',
          background: payBg,
          border: `1px solid ${payBorder}`,
          borderRadius: 8,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 16,
          alignItems: 'center',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10.5,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              color: '#64748b',
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Total Due
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            {formatCents(totalCents)}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 10.5,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              color: '#64748b',
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Amount Paid
          </div>
          {canEditMetadata ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: '#64748b', fontWeight: 600 }}>$</span>
              <input
                ref={paidInputRef}
                type="number"
                value={centsToDollarString(draftedPaidCents)}
                min={0}
                step={0.01}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => setPaidDraftCents(dollarStringToCents(event.target.value))}
                onBlur={() => {
                  if (paidDraftCents !== null) commitPaid(paidDraftCents)
                  onPaidBlur?.()
                }}
                style={{
                  width: 110,
                  height: 30,
                  border: `1px solid ${payBorder}`,
                  background: 'white',
                  borderRadius: 6,
                  padding: '0 8px',
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'right',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          ) : (
            <div style={{ fontSize: 16, fontWeight: 700, color: payAccent }}>
              {formatCents(paidCents)}
            </div>
          )}
        </div>
        {draftedOutstandingCents > 0 ? (
          <div>
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                color: '#64748b',
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Outstanding
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#c2410c' }}>
              {formatCents(draftedOutstandingCents)}
            </div>
          </div>
        ) : null}
      </div>

      {invoice.paidInfo || (fullyPaid && invoice.paidDate) ? (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: '#ecfdf5',
            borderRadius: 6,
            fontSize: 12,
            color: '#065f46',
          }}
        >
          ✓{' '}
          {invoice.paidInfo
            ? invoice.paidInfo
            : `Paid on ${invoice.paidDate} — ${formatCents(paidCents)} received`}
        </div>
      ) : null}

      {invoice.note ? (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: '#64748b',
            fontStyle: 'italic',
          }}
        >
          Note: {invoice.note}
        </div>
      ) : null}
    </>
  )
}
